import AuthoritySelect from 'Components/AuthoritySelect'
import CustomDateTimePicker from 'Components/CustomDateTimePicker'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import LoadingSelect from 'Components/LoadingSelect'
import StyledForm from 'Components/StyledForm'
import { WithLoader } from 'Components/WithLoader'
import ZoneSelect from 'Components/ZoneSelect'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import TicketSaleTargetSelect from 'Services/SalesManagement/Features/TicketSale/Components/TicketSaleTargetSelect'
import { useSalesChannelData } from 'Services/SalesManagement/Features/TicketSale/Hooks/useSalesChannelData'
import { SELECTED_TICKET_SALE_PARAMETER_KEY } from 'Services/SalesManagement/Features/TicketSale/Hooks/useTicketSalesParams'
import { useGetSalesChannelProductsByAgeGroupQuery } from 'Services/SalesManagement/Features/TicketSale/salesChannelApi'
import {
  useCreateSingleTicketSaleMutation,
  useGetAllTicketSaleTargetsQuery,
  useSendTicketSaleEmailReceiptMutation
} from 'Services/SalesManagement/Features/TicketSale/ticketSaleApi'
import type { TicketSale } from 'Services/SalesManagement/Features/TicketSale/types'
import type { FieldValidationErrors } from 'Services/SalesManagement/types'
import useTypedSelector from 'Store'
import { QR_TICKET_GROUP_MAX_SIZE, QR_TICKET_GROUP_MIN_SIZE } from 'Util/constants'
import { formatToUTCISO } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField
} from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

const fieldErrorsDefaultState: FieldValidationErrors = {
  id: [],
  transaction_id: [],
  target: [],
  target_email: [],
  age_group_id: [],
  product_id: [],
  product_name: [],
  zone_from_id: [],
  zone_to_id: [],
  validity_starts: [],
  validity_ends: [],
  group_size: [],
  authority: [],
  ticket_data: [],
  created: [],
  updated: []
}

interface TicketSaleFormProps {
  successBaseUrl: string
  ticketSale?: TicketSale
}

export default function TicketSaleForm({
  successBaseUrl,
  ticketSale
}: Readonly<TicketSaleFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!ticketSale

  const { user } = useTypedSelector(state => state.user)
  const { authorities } = useAuthorityContext()

  const [ticketSaleDetails, setTicketSaleDetails] = useState<Partial<TicketSale>>(
    (isEdit && ticketSale) || {
      authority: isSuperuser(user) ? undefined : user?.pta
    }
  )

  const { singleSalesChannel, salesChannelInfo, isSalesChannelInfoFetching } = useSalesChannelData(
    ticketSaleDetails.authority
  )

  const { data: products, isFetching: isProductsFetching } =
    useGetSalesChannelProductsByAgeGroupQuery(
      {
        salesChannelId: singleSalesChannel?.external_id as string,
        ageGroupId: ticketSaleDetails.age_group_id as number
      },
      { skip: !ticketSaleDetails.age_group_id || !singleSalesChannel?.external_id }
    )

  const { data: ticketSaleTargets, isFetching: isTicketSaleTargetsFetching } =
    useGetAllTicketSaleTargetsQuery({ disable_pagination: true })

  const [createSingleTicketSale, { isLoading: isCreateSingleTicketLoading }] =
    useCreateSingleTicketSaleMutation()

  const [sendTicketSaleEmailReceipt, { isLoading: isSendEmailReceiptLoading }] =
    useSendTicketSaleEmailReceiptMutation()

  const [fieldErrors, setFieldErrors] = useState(fieldErrorsDefaultState)

  const handleChange = (value: string | string[] | number | undefined, field: keyof TicketSale) => {
    if (value !== undefined) {
      setTicketSaleDetails(prevState => ({ ...prevState, [field]: value }))
      setFieldErrors(prevState => ({ ...prevState, [field]: [] }))
    }
  }

  const handleZoneChange = (e: SelectChangeEvent<unknown>, _child: React.ReactNode) => {
    const zoneId = e.target.value
    const zoneName = salesChannelInfo?.zones.find(zone => zone.id === zoneId)?.name ?? ''
    const fieldName = e.target.name
    setTicketSaleDetails(prevState => ({
      ...prevState,
      [`${fieldName}_id`]: zoneId,
      [`${fieldName}_name`]: zoneName
    }))
    setFieldErrors(prevState => ({
      ...prevState,
      [`${fieldName}_id`]: []
    }))
  }

  const handleProductChange = (e: SelectChangeEvent<unknown>, _child: React.ReactNode) => {
    const productId = e.target.value as string
    const productName = products?.find(product => product.id === productId)?.name ?? ''
    setTicketSaleDetails(prevState => ({
      ...prevState,
      product_id: productId,
      product_name: productName
    }))
    setFieldErrors(prevState => ({ ...prevState, product_id: [], product_name: [] }))
  }

  const handleAgeGroupChange = (value: number) => {
    setTicketSaleDetails(prevState => ({
      ...prevState,
      age_group_id: value,
      // clear the selected product when the age group changes
      product_id: undefined,
      product_name: undefined
    }))
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setFieldErrors(fieldErrorsDefaultState)
    const ticketSaleData = {
      ...ticketSaleDetails,
      price: 0
    }

    createSingleTicketSale(ticketSaleData)
      .unwrap()
      .then(result => {
        // Send email receipt with a separate request after the ticket sale is created
        sendTicketSaleEmailReceipt(result.id)
          .unwrap()
          .catch(() => {
            SnackbarUtils.error(t`Failed to send the email receipt.`)
          })
          .finally(() => {
            SnackbarUtils.success(t`Ticket successfully sold.`)
            navigate(
              appendQueryParamsToUrl(successBaseUrl, {
                [SELECTED_TICKET_SALE_PARAMETER_KEY]: result.id
              })
            )
          })
      })
      .catch(err => {
        if (err && 'data' in err) {
          const validationErrors = err.data as FieldValidationErrors
          if (validationErrors) {
            setFieldErrors(prevState => ({
              ...prevState,
              ...validationErrors
            }))
          } else {
            SnackbarUtils.error(t`There was an error selling the ticket.`)
          }
        }
      })
  }

  const handleDateTimePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = formatToUTCISO(value)
    }
    handleChange(formattedValue, name as keyof TicketSale)
  }

  const isLoading = isSalesChannelInfoFetching || isTicketSaleTargetsFetching

  const isSubmitLoading = isCreateSingleTicketLoading || isSendEmailReceiptLoading

  return (
    <WithLoader loading={isLoading}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
            {isSuperuser(user) && (
              <Box>
                <AuthoritySelect
                  sx={{ minWidth: 200 }}
                  value={ticketSaleDetails.authority ?? ''}
                  onChange={value => handleChange(value, 'authority')}
                  authorities={authorities}
                  error={fieldErrors.authority[0]}
                />
              </Box>
            )}
            <Box>
              <FormControl sx={{ minWidth: 200 }} required>
                <InputLabel>{t`Age group`}</InputLabel>
                <Select
                  name="age_group_id"
                  required
                  label={t`Age group`}
                  value={ticketSaleDetails.age_group_id ?? ''}
                  onChange={e => handleAgeGroupChange(Number(e.target.value))}
                >
                  {salesChannelInfo?.age_groups.map(({ id, name }) => (
                    <MenuItem key={id} value={id}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <ZoneSelect
                label={t`Zone from`}
                name="zone_from"
                value={ticketSaleDetails.zone_from_id ?? ''}
                onChange={handleZoneChange}
                error={fieldErrors.zone_from_id[0]}
                zones={salesChannelInfo?.zones ?? []}
                required
              />
              <Box>{` - `}</Box>
              <ZoneSelect
                label={t`Zone to`}
                name="zone_to"
                value={ticketSaleDetails.zone_to_id ?? ''}
                onChange={handleZoneChange}
                error={fieldErrors.zone_to_id[0]}
                zones={salesChannelInfo?.zones ?? []}
                required
              />
            </Box>
            <Box>
              <FormControl sx={{ minWidth: 200 }} required>
                <InputLabel>{t`Ticket`}</InputLabel>
                <LoadingSelect
                  name="product_id"
                  required
                  label={t`Ticket`}
                  value={ticketSaleDetails.product_id ?? ''}
                  onChange={handleProductChange}
                  error={fieldErrors.product_id.length > 0}
                  isLoading={isProductsFetching}
                >
                  {products?.map(({ id, name }) => (
                    <MenuItem key={id} value={id}>
                      {name}
                    </MenuItem>
                  ))}
                </LoadingSelect>
                <FormHelperText error={fieldErrors.product_id.length > 0}>
                  {fieldErrors.product_id.length > 0 ? fieldErrors.product_id[0] : ''}
                </FormHelperText>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <CustomDateTimePicker
                name="validity_starts"
                value={
                  ticketSaleDetails.validity_starts
                    ? new Date(ticketSaleDetails.validity_starts)
                    : null
                }
                dateToCompare={
                  ticketSaleDetails.validity_ends ? new Date(ticketSaleDetails.validity_ends) : null
                }
                dateToCompareFieldName="validity_ends"
                isStartDate={true}
                onChange={handleDateTimePickerChange}
                label={t`Valid from`}
                isRequired={true}
                isError={fieldErrors.validity_starts.length > 0}
                helperText={
                  fieldErrors.validity_starts.length > 0 ? fieldErrors.validity_starts[0] : ''
                }
              />
              <Box pt={4}>{` - `}</Box>
              <CustomDateTimePicker
                name="validity_ends"
                value={
                  ticketSaleDetails.validity_ends ? new Date(ticketSaleDetails.validity_ends) : null
                }
                dateToCompare={
                  ticketSaleDetails.validity_starts
                    ? new Date(ticketSaleDetails.validity_starts)
                    : null
                }
                dateToCompareFieldName="validity_starts"
                isStartDate={false}
                onChange={handleDateTimePickerChange}
                label={t`Valid to`}
                isRequired={true}
                isError={fieldErrors.validity_ends.length > 0}
                helperText={
                  fieldErrors.validity_ends.length > 0 ? fieldErrors.validity_ends[0] : ''
                }
              />
            </Box>
            <Box>
              <TextField
                sx={{ minWidth: 200 }}
                name="group_size"
                required
                label={t`Group size`}
                type="number"
                slotProps={{
                  htmlInput: { min: QR_TICKET_GROUP_MIN_SIZE, max: QR_TICKET_GROUP_MAX_SIZE }
                }}
                value={ticketSaleDetails?.group_size ?? ''}
                error={fieldErrors.group_size.length > 0}
                helperText={fieldErrors.group_size.length > 0 ? fieldErrors.group_size[0] : ''}
                onChange={e => handleChange(e.target.value, 'group_size')}
              />
            </Box>
            <Box>
              <TicketSaleTargetSelect
                targets={ticketSaleTargets ?? []}
                sx={{ minWidth: 200 }}
                value={ticketSaleDetails.target ?? ''}
                onChange={value => handleChange(value, 'target')}
                error={fieldErrors.target[0]}
              />
            </Box>
            <Box>
              <TextField
                sx={{ minWidth: 300 }}
                name="target_email"
                required
                label={t`Target email`}
                type="email"
                value={ticketSaleDetails?.target_email ?? ''}
                error={fieldErrors.target_email.length > 0}
                helperText={fieldErrors.target_email.length > 0 ? fieldErrors.target_email[0] : ''}
                onChange={e => handleChange(e.target.value, 'target_email')}
              />
            </Box>
            <CustomLoadingButton
              isLoading={isSubmitLoading}
              type="submit"
              size="large"
              iconPosition="end"
            >
              {t`Sell ticket`}
            </CustomLoadingButton>
          </StyledForm>
        </Grid>
      </Grid>
    </WithLoader>
  )
}
